import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as DatePicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  Calendar as CalIcon,
  MapPin,
  Bell,
  ClipboardCheck,
  Users,
  Pencil,
  Trash2,
  MessageSquareText,
  ImageIcon,
  Upload,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";

// Fixed-order hue rotation, same slots the dashboard uses — a glance at any
// list in the app draws from the same palette.
const ROW_HUES = ["bg-fun-blue", "bg-fun-orange", "bg-fun-aqua", "bg-fun-violet", "bg-fun-magenta", "bg-fun-green"];

const NO_TEMPLATE = "__none__"; // Select can't hold an empty-string item value

// What to send the moment a lead confirms registration.
const REG_MODES = [
  { value: "none", label: "Nothing" },
  { value: "text", label: "Text only" },
  { value: "image", label: "Image only" },
  { value: "both", label: "Text + image" },
];

const empty = {
  name: "",
  date: "",
  venue: "",
  description: "",
  reminder_hours_before: 24,
  registration_message: "",
  registration_message_image_url: "",
  reminder_template_id: NO_TEMPLATE,
};

/** Derive the mode selector from the two stored fields — so editing an event
 * created before this selector existed still shows the right option. */
function regModeFrom(ev) {
  const hasText = !!(ev.registration_message || "").trim();
  const hasImage = !!(ev.registration_message_image_url || "").trim();
  if (hasText && hasImage) return "both";
  if (hasImage) return "image";
  if (hasText) return "text";
  return "none";
}

/** <input type="datetime-local"> needs "YYYY-MM-DDTHH:mm" in the date's own offset. */
function toLocalInput(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : "";
}

/** form.date stays "YYYY-MM-DDTHH:mm" internally (unchanged by the rest of the
 * form/API) — these just split it into a Date for the calendar picker and a
 * "HH:mm" string for the time input, and join them back. */
function splitLocalInput(value) {
  const [datePart, timePart] = (value || "").split("T");
  const date = datePart ? new Date(`${datePart}T00:00:00`) : undefined;
  return { date, time: timePart || "" };
}
function joinLocalInput(date, time) {
  if (!date) return "";
  const datePart = format(date, "yyyy-MM-dd");
  return `${datePart}T${time || "00:00"}`;
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null); // event being edited, null = creating
  const [loading, setLoading] = useState(false);
  const [regMode, setRegMode] = useState("none");
  // Pending upload: a File picked but not yet sent, plus a local object URL
  // for the preview. Cleared once the upload lands and the real URL is stored.
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  async function load() {
    const [{ data }, { data: tpls }] = await Promise.all([
      api.get("/events"),
      api.get("/templates"),
    ]);
    setEvents(data);
    setTemplates(tpls);
  }
  useEffect(() => {
    load();
  }, []);

  const approvedTemplates = templates.filter((t) => t.approval_status === "approved");

  function resetImagePick() {
    setImageFile(null);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  }

  function startCreate() {
    setEditing(null);
    setForm(empty);
    setRegMode("none");
    resetImagePick();
    setOpen(true);
  }

  function startEdit(ev) {
    setEditing(ev);
    setForm({
      name: ev.name || "",
      date: toLocalInput(ev.date),
      venue: ev.venue || "",
      description: ev.description || "",
      reminder_hours_before: ev.reminder_hours_before ?? 24,
      registration_message: ev.registration_message || "",
      registration_message_image_url: ev.registration_message_image_url || "",
      reminder_template_id: ev.reminder_template_id || NO_TEMPLATE,
    });
    setRegMode(regModeFrom(ev));
    resetImagePick();
    setOpen(true);
  }

  function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    resetImagePick();
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  async function submit(e) {
    e.preventDefault();
    if (!splitLocalInput(form.date).date) {
      return toast.error("Choose a date");
    }
    if (regMode === "image" || regMode === "both") {
      if (!imageFile && !form.registration_message_image_url) {
        return toast.error("Choose an image to upload");
      }
    }
    if ((regMode === "text" || regMode === "both") && !form.registration_message.trim()) {
      return toast.error("Enter the message text");
    }

    setLoading(true);
    try {
      let imageUrl = regMode === "image" || regMode === "both"
        ? form.registration_message_image_url
        : "";
      if ((regMode === "image" || regMode === "both") && imageFile) {
        setUploadingImage(true);
        const fd = new FormData();
        fd.append("file", imageFile);
        const { data } = await api.post("/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imageUrl = data.url;
        setUploadingImage(false);
        // Saved fine, but the WhatsApp send will fail later if the backend's
        // public URL isn't reachable — warn now instead of a confusing
        // failure at send time.
        if (data.warning) toast.warning(data.warning, { duration: 10000 });
      }

      const payload = {
        ...form,
        date: new Date(form.date).toISOString(),
        reminder_template_id:
          form.reminder_template_id === NO_TEMPLATE ? "" : form.reminder_template_id,
        registration_message: regMode === "text" || regMode === "both" ? form.registration_message : "",
        registration_message_image_url: imageUrl,
      };
      if (editing) {
        await api.patch(`/events/${editing.id}`, payload);
        toast.success("Programme updated");
      } else {
        await api.post("/events", payload);
        toast.success("Programme created");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      resetImagePick();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  }

  async function remove(ev) {
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success(`"${ev.name}" deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function sendReminders(id) {
    try {
      await api.post(`/events/${id}/send-reminders`);
      toast.success("Reminders queued to confirmed attendees");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10" data-testid="events-page">
      <PageHeader
        overline="Programmes"
        title="Events"
        subtitle="Every campaign, workflow and report is tied to a programme."
        actions={
          <Dialog
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) {
                setEditing(null);
                resetImagePick();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={startCreate} data-testid="new-event-btn">
                <Plus className="mr-2 h-4 w-4" /> New programme
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit programme" : "Create programme"}</DialogTitle>
                <DialogDescription>
                  Programmes group every lead, campaign and message under one event.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="new-event-form">
                <div className="space-y-2">
                  <Label>Programme name</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Wealth Seminar - Nov Batch"
                    data-testid="event-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-start font-normal"
                          data-testid="event-date-input"
                        >
                          <CalIcon className="mr-2 h-4 w-4 text-fun-orange" />
                          {splitLocalInput(form.date).date
                            ? format(splitLocalInput(form.date).date, "dd/MM/yyyy")
                            : "dd/mm/yyyy"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <DatePicker
                          mode="single"
                          selected={splitLocalInput(form.date).date}
                          onSelect={(d) => {
                            if (!d) return;
                            setForm({ ...form, date: joinLocalInput(d, splitLocalInput(form.date).time) });
                            setDateOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Time</Label>
                    <Input
                      required
                      type="time"
                      value={splitLocalInput(form.date).time}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          date: joinLocalInput(splitLocalInput(form.date).date || new Date(), e.target.value),
                        })
                      }
                      data-testid="event-time-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reminder (hours before)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.reminder_hours_before}
                    onChange={(e) =>
                      setForm({ ...form, reminder_hours_before: Number(e.target.value) })
                    }
                    data-testid="event-reminder-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Venue / link</Label>
                  <Input
                    value={form.venue}
                    onChange={(e) => setForm({ ...form, venue: e.target.value })}
                    placeholder="Grand Hyatt, Pune"
                    data-testid="event-venue-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="event-desc-input"
                  />
                </div>

                <div className="space-y-4 rounded-lg border border-dashed border-border p-4">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <MessageSquareText className="h-3.5 w-3.5" /> Registration message
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">
                    Sent the moment a lead confirms ("YES") for this programme — direct, no
                    approved template needed, since they just replied and the 24h window is open.
                  </p>

                  <div className="space-y-2">
                    <Label>Send</Label>
                    <Select value={regMode} onValueChange={setRegMode}>
                      <SelectTrigger data-testid="event-registration-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REG_MODES.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(regMode === "text" || regMode === "both") && (
                    <div className="space-y-2">
                      <Label>Message text</Label>
                      <Textarea
                        rows={3}
                        value={form.registration_message}
                        onChange={(e) => setForm({ ...form, registration_message: e.target.value })}
                        placeholder="Thanks {{name}}! You're registered for {{event}} on {{date}} at {{venue}}."
                        data-testid="event-registration-message-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Variables: {"{{name}}"}, {"{{event}}"}, {"{{date}}"}, {"{{venue}}"}
                        {regMode === "both" && " — sent as the image's caption."}
                      </p>
                    </div>
                  )}

                  {(regMode === "image" || regMode === "both") && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5" /> Image
                      </Label>
                      {imagePreview ? (
                        <div className="relative inline-block">
                          <img
                            src={imagePreview}
                            alt="Selected upload"
                            className="h-32 w-32 rounded-lg border border-border object-cover"
                          />
                          <button
                            type="button"
                            onClick={resetImagePick}
                            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-fun-red text-white shadow-md"
                            data-testid="event-registration-image-clear"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : form.registration_message_image_url ? (
                        <div className="relative inline-block">
                          <img
                            src={form.registration_message_image_url}
                            alt="Current upload"
                            className="h-32 w-32 rounded-lg border border-border object-cover"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setForm({ ...form, registration_message_image_url: "" })
                            }
                            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-fun-red text-white shadow-md"
                            data-testid="event-registration-image-clear"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-border text-muted-foreground hover:border-fun-blue hover:text-fun-blue">
                          <Upload className="h-5 w-5" />
                          <span className="text-[11px]">Upload image</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={onPickImage}
                            data-testid="event-registration-image-input"
                          />
                        </label>
                      )}
                      <p className="text-xs text-muted-foreground">
                        JPEG, PNG or WEBP, up to 5MB.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Bell className="h-3.5 w-3.5" /> Reminder template
                  </Label>
                  <Select
                    value={form.reminder_template_id}
                    onValueChange={(v) => setForm({ ...form, reminder_template_id: v })}
                  >
                    <SelectTrigger data-testid="event-reminder-template-select">
                      <SelectValue placeholder="Use the general reminder template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_TEMPLATE}>
                        Use the general reminder template (Settings)
                      </SelectItem>
                      {approvedTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Used when you click "Send reminders" below. Only approved templates can be
                    selected — a reminder must use a template since it can land outside the
                    24-hour window.
                  </p>
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={loading} data-testid="event-submit-btn">
                    {uploadingImage
                      ? "Uploading image…"
                      : loading
                        ? "Saving…"
                        : editing
                          ? "Save changes"
                          : "Create programme"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="mt-8 border-border shadow-sm" data-testid="events-table">
        {events.length === 0 ? (
          <div className="p-16 text-center">
            <CalIcon className="mx-auto h-10 w-10 text-fun-orange" />
            <div className="mt-4 font-heading text-lg font-semibold">No programmes yet</div>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              Programmes group every lead, campaign and message under one event.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-6 text-xs uppercase tracking-widest">
                  Programme
                </TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Funnel</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-widest">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((ev, i) => (
                <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                  <TableCell className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${ROW_HUES[i % ROW_HUES.length]}`}
                      >
                        {ev.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold">{ev.name}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" /> {ev.venue || "TBD"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="text-sm">{safeDate(ev.date)}</div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                      {ev.reminder_hours_before}h reminder
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center gap-2 text-xs">
                      <Chip label="Total" value={ev.stats?.total} />
                      <Chip label="Confirmed" value={ev.stats?.confirmed} tone="primary" />
                      <Chip label="Attended" value={ev.stats?.attended} tone="mint" />
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        data-testid={`event-attendance-${ev.id}`}
                      >
                        <Link to={`/events/${ev.id}/attendance`}>
                          <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" /> Attendance
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sendReminders(ev.id)}
                        title="Send reminders to confirmed attendees"
                        data-testid={`event-remind-${ev.id}`}
                      >
                        <Bell className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        title="View this programme's leads"
                        data-testid={`event-leads-${ev.id}`}
                      >
                        <Link to={`/leads?event_id=${ev.id}`}>
                          <Users className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(ev)}
                        title="Edit programme"
                        data-testid={`event-edit-${ev.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <ConfirmDialog
                        testId={`event-delete-dialog-${ev.id}`}
                        title={`Delete "${ev.name}"?`}
                        description={
                          `This removes the programme, its ${ev.stats?.total ?? 0} lead link(s) ` +
                          "and its campaigns. The leads themselves are kept. This cannot be undone."
                        }
                        onConfirm={() => remove(ev)}
                        trigger={
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete programme"
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            data-testid={`event-delete-${ev.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        }
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Chip({ label, value, tone = "" }) {
  const toneCls =
    tone === "primary"
      ? "bg-fun-aqua-tint text-fun-aqua"
      : tone === "mint"
        ? "bg-fun-blue-tint text-fun-blue"
        : "bg-muted text-foreground/80";
  return (
    <Badge className={`${toneCls} rounded-full px-2.5 py-0.5 text-[11px] font-semibold`}>
      {label}: {value ?? 0}
    </Badge>
  );
}

function safeDate(d) {
  try {
    return format(new Date(d), "PPP p");
  } catch {
    return d;
  }
}
